import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './components/PublicHeader';
import fleetVid from './assets/fleet_vid.mp4';
import './Landing.css';

const FLEET_VIDEO_URL = process.env.REACT_APP_STORAGE_URL
  ? `${process.env.REACT_APP_STORAGE_URL}/fleet_vid.mp4`
  : fleetVid;

function Landing({ onLoginClick }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  return (
    <div className="landing">
      <PublicHeader onLoginClick={onLoginClick} />

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="hero-title-main">AGRICULTURAL</span>
            <span className="hero-title-main">LOGISTICS</span>
            <span className="hero-title-sub">SERVICE</span>
          </h1>
          <p className="hero-tagline">From farm to market — transport your produce with confidence</p>
          <a href="#services" className="btn-learn">LEARN MORE</a>
        </div>
      </section>

      {/* Fleet Video */}
      <section className="landing-video">
        <div className="landing-video-inner">
          <video
            ref={videoRef}
            src={FLEET_VIDEO_URL}
            controls
            playsInline
            preload="metadata"
            className="landing-video-player"
            aria-label="FarmFleet introduction video"
          />
        </div>
      </section>

      {/* About */}
      <section id="about" className="about">
        <div className="about-inner">
          <h2 className="about-title">
            ABOUT <span className="accent">FARMFLEET</span>
          </h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                FarmFleet is a digital logistics platform designed to help farmers transport 
                agricultural produce from farms to markets using organized transport services.
              </p>
              <p>
                We connect farmers with reliable transport through our platform — request pickup,
                receive a price estimate, and follow order status as dispatch and drivers progress your trip.
              </p>
              <div className="about-mission-vision-landing">
                <Link to="/mission" className="mission-vision-card">
                  <h4>Our Mission</h4>
                  <p>To empower farmers with reliable, affordable, and accessible transport — so every harvest reaches the market.</p>
                  <span className="link-arrow">Learn more →</span>
                </Link>
                <Link to="/vision" className="mission-vision-card">
                  <h4>Our Vision</h4>
                  <p>A future where no farmer loses produce to transport barriers — where every harvest reaches its market.</p>
                  <span className="link-arrow">Learn more →</span>
                </Link>
              </div>
            </div>
            <div className="about-image">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/e/ed/Transport_de_marchandises_par_tricycle_01.jpg" 
                alt="Tricycle transporting goods in Douala, Cameroon" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services">
        <div className="services-inner">
          <div className="service-card">
            <h3>Transport Requests</h3>
            <p>Request transport via WhatsApp in English, French, or Pidgin. Complete the guided flow and receive an order confirmation when dispatch accepts your request.</p>
          </div>
          <div className="service-card">
            <h3>Dispatch Dashboard</h3>
            <p>Administrators view incoming orders, assign drivers, and see trip status updates on the web dashboard as operations move forward.</p>
          </div>
          <div className="service-card">
            <h3>Trip Tracking</h3>
            <p>Trip records with pickup and delivery confirmation (driver WhatsApp and dashboard). Order status moves from pending through to delivered.</p>
          </div>
          <div className="service-card">
            <h3>Smart Pricing</h3>
            <p>Personalized quotes from your route, load, and cold-chain needs — shown in WhatsApp before you confirm an order, not a one-size-fits-all table.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="footer-inner">
          <p className="footer-brand">FARMFLEET</p>
          <p className="footer-tagline">Agricultural Logistics Platform</p>
          <p className="footer-scope-note">
            Milestone 1 core: WhatsApp ordering and admin dispatch dashboard. Pages here are for
            information and contact.
          </p>
          <p className="footer-contact">Contact: +237 6 51 41 27 72</p>
          <div className="footer-quick-links">
            <span className="footer-quick-links-title">Quick Links</span>
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/about">About</Link>
              <Link to="/mission">Mission</Link>
              <Link to="/vision">Vision</Link>
              <Link to="/services">Services</Link>
              <Link to="/contact">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
